import React from "react";
import {
  Button,
  Checkbox,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../components/formsComponents";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import produce from "immer";
import { IBusinessPolicyChangeStepsProps } from "../PolicyChange.types";
import { ConfirmDialog } from "../../../../../../../components/Dialogs/ConfirmDialog";
import { AddOutlined } from "@mui/icons-material";
import { IBusinessPolicyToolFloater } from "../../../../../../../redux/businessPolicy/businessPolicyToolFloater/businesPolicyToolFloater.types";
import { ToolFloaterRightPanel } from "../../ToolFloater/ToolFloaterRightPanel";
import { useBusinessPolicyToolFloater } from "../../hooks/useBusinessPolicyToolFloater";

export const ToolFloaterChange: React.FC<IBusinessPolicyChangeStepsProps> = ({
  values,
  setValues,
  errors,
  handleChange,
  setFieldValue,
  customerId,
  customerPolicyId,
  isStatusCompleted,
  autoSaveData = () => {},
}) => {
  const [open, setOpen] = React.useState<{
	type: "create" | "edit" | "view";
	index?: number;
	data?: IBusinessPolicyToolFloater;
  } | null>(null);
  const theme = useTheme();
  const { toolFloaterDropdown, refreshData } =
	useBusinessPolicyToolFloater(customerPolicyId);

  const handleCheckBoxChange = (value: string) => {
	setFieldValue("tool_floater_change.type", value);
  };

  const handleSaveSuccess = (toolFloater: IBusinessPolicyToolFloater) => {
	const newValues = produce(values, (draftValues) => {
	  const updatedData = {
		...toolFloater,
		customer_policy_id: customerPolicyId ?? null,
		customer_id: customerId ? Number(customerId) : null,
	  };
	  if (open && open.type === "edit" && open.index !== undefined) {
		draftValues.tool_floater_change.records[open.index] = updatedData;
	  } else {
		draftValues.tool_floater_change.records.push(updatedData);
	  }
	});
	autoSaveData(newValues);
	setValues(newValues);
	setOpen(null);
	refreshData();
  };

  const handleCreateEdit = (index?: number) => () => {
	if (isStatusCompleted) {
	  return;
	}
	setOpen({
	  type: index !== undefined ? "edit" : "create",
	  index: index,
	  data:
		index !== undefined
		  ? values.tool_floater_change.records[index]
		  : undefined,
	});
  };

  const handleView = (index: number) => () => {
	setOpen({
	  type: "view",
	  index: index,
	  data: values.tool_floater_change.records[index],
	});
  };

  const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);
  const handleDelete = (index: number) => () => {
	setDeleteConfirm(index);
  };

  const handleDeleteConfirm = () => {
	if (deleteConfirm === null) {
	  return;
	}
	const newValues = produce(values, (draftValues) => {
	  draftValues.tool_floater_change.records.splice(deleteConfirm, 1);
	});
	setValues(newValues);
	autoSaveData(newValues);
	setDeleteConfirm(null);
  };

  return (
	<>
	  <LabelCard heading="Tool Floater Change" />
	  <Grid container spacing={2} mb={3}>
		<Grid item xs={12} lg={2.4}>
		  <Stack direction={"row"} alignItems={"center"}>
			<Checkbox
			  checked={values.tool_floater_change.type === "Add"}
			  onChange={(e) => {
				handleCheckBoxChange(e.target.checked ? "Add" : "");
			  }}
			/>
			<Typography>Add</Typography>
		  </Stack>
		</Grid>
		<Grid item xs={12} lg={2.4}>
		  <Stack direction={"row"} alignItems={"center"}>
			<Checkbox
			  checked={values.tool_floater_change.type === "Delete"}
			  onChange={(e) => {
				handleCheckBoxChange(e.target.checked ? "Delete" : "");
			  }}
			/>
			<Typography>Delete</Typography>
		  </Stack>
		</Grid>
		<Grid item xs={12} lg={2.4}>
		  <Stack direction={"row"} alignItems={"center"}>
			<Checkbox
			  checked={values.tool_floater_change.type === "Change"}
			  onChange={(e) => {
				handleCheckBoxChange(e.target.checked ? "Change" : "");
			  }}
			/>
			<Typography>Substitution</Typography>
		  </Stack>
		</Grid>
	  </Grid>

	  {values.tool_floater_change.type &&
		["Add", "Change"].includes(values.tool_floater_change.type) && (
		  <>
			<Grid container spacing={3}>
			  {values.tool_floater_change.type === "Change" && (
				<Grid item xs={12} md={6}>
				  <CustomFormLabel>Delete Tool Floater</CustomFormLabel>
				  <ControlledCustomSelect
					variant="outlined"
					size="small"
					type="string"
					name="tool_floater_change.delete_tool_floater"
					fullWidth
					displayEmpty
					value={Number(values.tool_floater_change.delete_tool_floater)}
					onChange={handleChange}
					placeholder="Select One"
					options={toolFloaterDropdown}
				  ></ControlledCustomSelect>
				</Grid>
			  )}
			  <Grid item xs={12}>
				<Stack direction={"row"} justifyContent={"end"}>
				  <Button
					variant="contained"
					disabled={isStatusCompleted}
					onClick={handleCreateEdit()}
					startIcon={<AddOutlined />}
				  >
					New
				  </Button>
				</Stack>
				<TableContainer>
				  <Table>
					<TableHead>
					  <TableRow>
						<TableCell>View</TableCell>
						<TableCell>Location #</TableCell>
						<TableCell>Effective Date</TableCell>
						<TableCell>Serial Number</TableCell>
						<TableCell>Limit Amount</TableCell>
					  </TableRow>
					</TableHead>
					<TableBody>
					  {values.tool_floater_change.records.map((item, index) => {
						return (
						  <TableRow key={index}>
							<TableCell sx={{ display: "flex" }}>
							  <StandardTableActions
								onViewClick={handleView(index)}
								onEditClick={
								  !isStatusCompleted
									? handleCreateEdit(index)
									: undefined
								}
								// onDeleteClick={
								//   !isStatusCompleted
								// 	? handleDelete(index)
								// 	: undefined
								// }
							  />
							</TableCell>
							<TableCell>{index + 1}</TableCell>
							<TableCell>{item.effective_date}</TableCell>
							<TableCell>{item.serial_number}</TableCell>
							<TableCell>{item.limit_amount}</TableCell>
						  </TableRow>
						);
					  })}
					</TableBody>
				  </Table>
				</TableContainer>
			  </Grid>
			</Grid>
		  </>
		)}

	  {values.tool_floater_change.type &&
		["Delete"].includes(values.tool_floater_change.type) && (
		  <Grid container>
			<Grid item xs={12} md={6}>
			  <CustomFormLabel>Delete Tool Floater</CustomFormLabel>
			  <ControlledCustomSelect
				variant="outlined"
				size="small"
				type="string"
				name="tool_floater_change.delete_tool_floater"
				fullWidth
				displayEmpty
				value={Number(values.tool_floater_change.delete_tool_floater)}
				onChange={handleChange}
				placeholder="Select One"
				options={toolFloaterDropdown}
			  ></ControlledCustomSelect>
			</Grid>
		  </Grid>
		)}

	  {open && (
		<ToolFloaterRightPanel
		  open={true}
		  readonly={open.type === "view"}
		  customerId={customerId}
		  customerPolicyId={customerPolicyId}
		  onSaveSuccess={handleSaveSuccess}
		  toolFloaterNumber={open.data ? open.data.incremental_number as any : undefined}
		  //staticStatusValue="INACTIVE"
		  created_from="Policy_Change"
		  onClose={() => setOpen(null)}
		  //initialData={open.data}
		  //disableApiCalls
		/>
	  )}
	  {deleteConfirm !== null && (
		<ConfirmDialog
		  open
		  content="Are you sure you want to delete this record?"
		  onClose={() => setDeleteConfirm(null)}
		  onConfrim={handleDeleteConfirm}
		/>
	  )}
	</>
  );
};
